import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { API } from "@/constants/roles";
import { MeltoLogo } from "@/components/MeltoLogo";

const formatError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || String(e)).join(" ");
  return "Something went wrong. Try again.";
};

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { token, password });
      setDone(true);
      setTimeout(() => navigate("/login"), 2000);
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-2xl border border-slate-200 bg-white px-4 py-3.5 text-sm font-medium text-slate-900 placeholder:text-slate-400 focus:border-cyan-400 focus:outline-none focus:ring-4 focus:ring-cyan-500/10 transition-all";

  return (
    <div data-testid="reset-password-page" className="relative flex min-h-screen items-center justify-center px-4">
      <div className="bg-grid-cyan bg-grid-fade absolute inset-0" aria-hidden="true" />
      <div className="relative w-full max-w-md">
        <div className="rounded-3xl border border-slate-200/80 bg-white p-8 shadow-xl shadow-slate-900/5 sm:p-10">
          {!token ? (
            <div className="text-center">
              <h1 data-testid="reset-invalid-heading" className="font-display text-xl font-bold text-slate-900">Invalid reset link</h1>
              <p className="mt-3 text-sm text-slate-600">This link is missing its token. Request a fresh one.</p>
              <Link to="/forgot-password" className="mt-5 inline-flex text-sm font-semibold text-cyan-600 hover:text-cyan-700">
                Request new link
              </Link>
            </div>
          ) : done ? (
            <div className="text-center">
              <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50">
                <CheckCircle2 className="h-6 w-6 text-emerald-500" />
              </span>
              <h1 data-testid="reset-done-heading" className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900">
                Password updated
              </h1>
              <p className="mt-3 text-sm text-slate-600">Redirecting you to login...</p>
            </div>
          ) : (
            <form data-testid="reset-password-form" onSubmit={handleSubmit}>
              <MeltoLogo size={48} className="rounded-2xl" testid="reset-logo-mark" />
              <h1 className="mt-6 font-display text-2xl font-bold tracking-tight text-slate-900">Choose a new password</h1>
              <p className="mt-2 text-sm text-slate-600">Minimum 8 characters.</p>
              <div className="mt-8 space-y-5">
                <div>
                  <label htmlFor="reset-password" className="mb-2 block text-xs font-semibold text-slate-700">New password</label>
                  <input
                    id="reset-password"
                    data-testid="reset-password-input"
                    type="password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="New password"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label htmlFor="reset-confirm" className="mb-2 block text-xs font-semibold text-slate-700">Confirm password</label>
                  <input
                    id="reset-confirm"
                    data-testid="reset-confirm-input"
                    type="password"
                    required
                    minLength={8}
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    placeholder="Repeat password"
                    className={inputCls}
                  />
                </div>
              </div>
              <button
                data-testid="reset-submit-btn"
                type="submit"
                disabled={loading}
                className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-cyan-500 py-3.5 text-sm font-semibold text-white shadow-lg shadow-cyan-500/25 transition-all duration-200 hover:bg-cyan-600 disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "Updating..." : "Update password"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
